import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import { compareCuratedRanks, getCuratedRank, usePrecomputedEmbeddings } from '@shared/utils/search';
import type { ColorHits } from './useColorHits';
import { colorwayOrderForSelection, EMPTY_COLORWAY_ORDER } from './useColorHits';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { COLOR_FACET, STYLE_FACET } from './components/Filters/Filters.constants';
import type { TemplateFacetIndex } from './templateFacetMatching.types';
import { groupSelections, partitionBySelections } from './templateFacetMatching';
import { useMemo } from 'react';

export type TagFilteredTemplates = Readonly<{
  templates: ReadonlyArray<StationeryTemplate>;
  /**
   * `themeId` → colorway ids matching the selected colors, best-first. Empty
   * unless a color filter is active; consumed by tiles to order swatches and
   * preselect the matching colorway, so a card filtered as "blue" renders blue.
   */
  colorwayOrder: ReadonlyMap<string, ReadonlyArray<string>>;
  /**
   * Whether the user has selected any color. Callers use this to stand down
   * behavior that would contradict the selection — notably the display-order
   * flag's repeated tiles, which each pre-select a colorway of their own.
   */
  isColorFilterActive: boolean;
  /** Whether the user has selected any style. */
  isStyleFilterActive: boolean;
  /**
   * Ids of the templates that match only some of the selected groups. They trail the exact
   * matches in `templates`; the gallery draws a divider before the first of them. Empty unless
   * two or more groups are selected.
   */
  partialMatchIds: ReadonlySet<string>;
}>;

const NO_PARTIAL_MATCHES: ReadonlySet<string> = new Set();

/**
 * Applies the `pf` facet selection: AND across groups, OR within a group (see
 * `templateFacetMatching`), with the designs matching only some groups filled in after the
 * exact matches (Amy, 2026-10-10) so a narrow combination doesn't leave the page near-empty.
 * The facet counts (`useFilterFacets`) use the same matcher and describe the exact matches.
 */
export const useTagFilteredTemplates = (templates: ReadonlyArray<StationeryTemplate>, colorHits: ColorHits, facetIndex: TemplateFacetIndex): TagFilteredTemplates => {
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const { themes } = usePrecomputedEmbeddings();

  const selectedColorOptions = useMemo(() => new Set([...activePrintFilters].filter(option => option.startsWith(`${COLOR_FACET}:`))), [activePrintFilters]);
  const selectedStyleOptions = useMemo(() => new Set([...activePrintFilters].filter(option => option.startsWith(`${STYLE_FACET}:`))), [activePrintFilters]);

  const { filteredTemplates, partialMatchIds } = useMemo(() => {
    if (!activePrintFilters.size) return { filteredTemplates: templates, partialMatchIds: NO_PARTIAL_MATCHES };

    const { exact, partial } = partitionBySelections(templates, groupSelections(activePrintFilters), facetIndex);

    // A single selected style can have an editorial order shared with text
    // embeddings search. Keep catalog order as the fallback for tied ranks.
    // Each tier is ranked on its own so partial matches never climb above exact ones.
    const selectedStyleName = selectedStyleOptions.size === 1 ? [...selectedStyleOptions][0].slice(`${STYLE_FACET}:`.length) : undefined;
    const rankTier = (tier: ReadonlyArray<StationeryTemplate>) => {
      if (!selectedStyleName || !themes) return tier;
      return tier
        .map((template, index) => ({ template, index, rank: getCuratedRank(selectedStyleName, { themeId: template.themeId }) }))
        .sort((a, b) => {
          const curatedRankDiff = compareCuratedRanks(a.rank, b.rank);
          if (curatedRankDiff !== 0) return curatedRankDiff;
          return a.index - b.index;
        })
        .map(({ template }) => template);
    };

    return {
      filteredTemplates: [...rankTier(exact), ...rankTier(partial)],
      partialMatchIds: partial.length ? new Set(partial.map(template => template.id)) : NO_PARTIAL_MATCHES,
    };
  }, [templates, activePrintFilters, facetIndex, selectedStyleOptions, themes]);

  const colorwayOrder = useMemo(
    () => (selectedColorOptions.size ? colorwayOrderForSelection(colorHits.colorways, selectedColorOptions) : EMPTY_COLORWAY_ORDER),
    [colorHits, selectedColorOptions],
  );

  return {
    templates: filteredTemplates,
    colorwayOrder,
    isColorFilterActive: selectedColorOptions.size > 0,
    isStyleFilterActive: selectedStyleOptions.size > 0,
    partialMatchIds,
  };
};
